/**
 * Shared Seat Map Viewer component
 * Used by both lawyer/seatmap.tsx and tools/seatmap.tsx
 */
import { useState, useEffect, useRef } from 'react'
import { Armchair, Search, Loader2, AlertCircle, Plane, Wifi, WifiOff } from 'lucide-react'
import { type SeatMapResponse, type CabinSection, type SeatRow, type SeatInfo } from '@/utils/api/lawyerAPI'
import { useAmadeusWebSocket, type QueueProgress, type SeatmapResult } from '@/contexts/AmadeusWebSocketContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

/**
 * Search params interface for seat map page
 */
export interface SeatMapSearchParams {
  airline?: string
  flight?: string
  class?: string
  date?: string
  origin?: string
  destination?: string
}

/**
 * Format date for Amadeus (DDMMM)
 */
function formatDateForAmadeus(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0')
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
                  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
  const month = months[date.getMonth()]
  return `${day}${month}`
}

/**
 * Get seat status color
 * Design: Available seats are prominent, unavailable seats fade into background
 */
function getSeatColor(status_code: string): string {
  switch (status_code) {
    // === AVAILABLE SEATS (Most Prominent) ===
    case 'available':
      return 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm'
    case 'available_pref':
      return 'bg-emerald-400 hover:bg-emerald-500 text-white shadow-sm'

    // === BOOKABLE SPECIAL SEATS (Secondary Prominence) ===
    case 'chargeable':
      return 'bg-amber-300 hover:bg-amber-400 text-amber-900'
    case 'preferred':
      return 'bg-sky-300 hover:bg-sky-400 text-sky-900'
    case 'legroom':
      return 'bg-cyan-300 hover:bg-cyan-400 text-cyan-900'
    case 'handicap':
      return 'bg-violet-300 hover:bg-violet-400 text-violet-900'
    case 'exit':
      return 'bg-teal-400 hover:bg-teal-500 text-white'

    // === BLOCKED/RESTRICTED (Visible but subdued) ===
    case 'blocked':
      return 'bg-red-100 text-red-300 border border-red-200'
    case 'restricted':
      return 'bg-rose-50 text-rose-300 border border-rose-100'

    // === UNAVAILABLE (Least Prominent - Fade into background) ===
    case 'occupied':
      return 'bg-slate-100 text-slate-300'
    case 'umnr':
      return 'bg-slate-100 text-slate-300'
    case 'infant':
      return 'bg-slate-100 text-slate-300'

    // === DEFAULT (Unknown status) ===
    default:
      return 'bg-slate-50 text-slate-200'
  }
}

/**
 * Single seat component
 */
function SeatCell({ seat }: { seat: SeatInfo | null; column: string }) {
  if (!seat) {
    // Empty seat position (no seat exists here)
    return <div className="w-10 h-10" />
  }

  const colorClass = getSeatColor(seat.status_code)
  const featureLabels = seat.features.map(f => {
    switch (f) {
      case 'wing': return 'Wing'
      case 'exit': return 'Exit'
      case 'bulkhead': return 'Bulkhead'
      case 'upper_deck': return 'Upper Deck'
      default: return ''
    }
  }).filter(Boolean)

  // Build tooltip: "12A - Available" or "12A - Available (Exit, Wing)"
  const seatNumber = `${seat.row}${seat.column}`
  const tooltip = featureLabels.length > 0
    ? `${seatNumber} - ${seat.status_label} (${featureLabels.join(', ')})`
    : `${seatNumber} - ${seat.status_label}`

  return (
    <div
      className={`w-10 h-10 rounded-md flex items-center justify-center text-xs font-semibold
                  cursor-default transition-all hover:scale-110 hover:z-10 hover:shadow-lg ${colorClass}`}
      title={tooltip}
    >
      {seat.status}
    </div>
  )
}

/**
 * Aisle indicator
 */
function Aisle() {
  return <div className="w-6" />
}

/**
 * Row component with seats and aisles
 */
function SeatRowDisplay({ row, columns, aisleAfter }: { row: SeatRow; columns: string[]; aisleAfter: string[] }) {
  // Create a map of column -> seat
  const seatMap: Record<string, SeatInfo | null> = {}
  for (const col of columns) {
    seatMap[col] = null
  }
  for (const seat of row.seats) {
    seatMap[seat.column] = seat
  }

  return (
    <div className="flex items-center gap-1 py-1">
      {/* Row number */}
      <div className="w-10 text-right text-sm text-slate-600 font-mono pr-2 font-medium">
        {row.row_number}
      </div>

      {/* Row features indicators */}
      <div className="w-8 flex items-center justify-center gap-0.5 text-[10px] text-slate-400">
        {row.is_exit && <span title="Exit row" className="text-teal-500 font-bold">E</span>}
        {row.is_bulkhead && <span title="Bulkhead" className="text-blue-400">B</span>}
        {row.is_wing && <span title="Wing" className="text-slate-400">W</span>}
        {row.is_upper_deck && <span title="Upper deck" className="text-purple-400">U</span>}
      </div>

      {/* Seats with aisles */}
      {columns.map((col) => (
        <div key={col} className="flex items-center">
          <SeatCell seat={seatMap[col]} column={col} />
          {aisleAfter.includes(col) && <Aisle />}
        </div>
      ))}

      {/* Row number on right side */}
      <div className="w-10 text-left text-sm text-slate-600 font-mono pl-2 font-medium">
        {row.row_number}
      </div>
    </div>
  )
}

/**
 * Column headers
 */
function ColumnHeaders({ columns, aisleAfter }: { columns: string[]; aisleAfter: string[] }) {
  return (
    <div className="flex items-center gap-1 py-2 border-b border-slate-200 mb-1">
      <div className="w-10" />
      <div className="w-8" />
      {columns.map((col) => (
        <div key={col} className="flex items-center">
          <div className="w-10 text-center text-sm font-semibold text-slate-700">
            {col}
          </div>
          {aisleAfter.includes(col) && <Aisle />}
        </div>
      ))}
      <div className="w-10" />
    </div>
  )
}

/**
 * Get cabin class display name
 */
function getCabinClassName(cabinClass: string): string {
  switch (cabinClass) {
    case 'F': return 'First Class'
    case 'J': return 'Business Class'
    case 'C': return 'Business Class'
    case 'W': return 'Premium Economy'
    case 'Y': return 'Economy Class'
    case 'M': return 'Economy Class'
    default: return `Class ${cabinClass}`
  }
}

/**
 * Cabin section display
 */
function CabinSectionDisplay({ section }: { section: CabinSection }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 mb-5 shadow-sm">
      {/* Cabin header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="font-semibold text-lg text-slate-900">
            {getCabinClassName(section.cabin_class)}
          </span>
          <span className="text-xs text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
            {section.rows.length} rows
          </span>
        </div>
      </div>

      {/* Centered seat map display */}
      <div className="flex justify-center overflow-x-auto">
        <div className="inline-block">
          {/* Column headers */}
          <ColumnHeaders columns={section.columns} aisleAfter={section.aisle_after} />

          {/* Seat rows */}
          <div className="max-h-[600px] overflow-y-auto">
            {section.rows.map((row) => (
              <SeatRowDisplay
                key={row.row_number}
                row={row}
                columns={section.columns}
                aisleAfter={section.aisle_after}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Legend component
 */
function SeatMapLegend() {
  const items = [
    // Available (prominent)
    { code: '.', label: 'Available', colorClass: 'bg-emerald-500 text-white shadow-sm' },
    { code: 'A', label: 'Available (Pref)', colorClass: 'bg-emerald-400 text-white shadow-sm' },
    // Bookable extras (secondary)
    { code: 'Y', label: 'Chargeable', colorClass: 'bg-amber-300 text-amber-900' },
    { code: 'V', label: 'Preferred', colorClass: 'bg-sky-300 text-sky-900' },
    { code: 'L', label: 'Extra Legroom', colorClass: 'bg-cyan-300 text-cyan-900' },
    { code: 'H', label: 'Handicap', colorClass: 'bg-violet-300 text-violet-900' },
    // Blocked (subdued)
    { code: 'X', label: 'Blocked', colorClass: 'bg-red-100 text-red-300 border border-red-200' },
    { code: '/', label: 'Restricted', colorClass: 'bg-rose-50 text-rose-300 border border-rose-100' },
    // Unavailable (faded)
    { code: '+', label: 'Occupied', colorClass: 'bg-slate-100 text-slate-300' },
  ]

  return (
    <div className="flex flex-wrap gap-4 text-sm">
      {items.map(item => (
        <div key={item.code} className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-semibold ${item.colorClass}`}>
            {item.code}
          </div>
          <span className="text-slate-600">{item.label}</span>
        </div>
      ))}
    </div>
  )
}

export interface SeatMapViewerProps {
  /** Search params from URL (for auto-fill from availability page) */
  searchParams: SeatMapSearchParams
  /** Active button color class (e.g., 'bg-action-500' for lawyer, 'bg-purple-500' for tools) */
  activeButtonClass?: string
}

export function SeatMapViewer({
  searchParams,
  activeButtonClass = 'bg-action-500'
}: SeatMapViewerProps) {
  // Use the shared Amadeus WebSocket connection
  const { status: wsStatus, sendSeatmapRequest } = useAmadeusWebSocket()

  const [airline, setAirline] = useState('')
  const [flightNumber, setFlightNumber] = useState('')
  const [bookingClass, setBookingClass] = useState('M')
  const [date, setDate] = useState(formatDateForAmadeus(new Date()))
  const [origin, setOrigin] = useState('')
  const [destination, setDestination] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<SeatMapResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [hasAutoSearched, setHasAutoSearched] = useState(false)
  const [queueStatus, setQueueStatus] = useState<string | null>(null)

  // Handle progress updates from queue
  const handleProgress = (progress: QueueProgress) => {
    if (progress.type === 'queue_status' && progress.queue_position && progress.queue_position > 1) {
      setQueueStatus(`Queued (position ${progress.queue_position})...`)
    } else if (progress.type === 'processing') {
      if (progress.page && progress.page > 1) {
        setQueueStatus(`Fetching page ${progress.page}...`)
      } else {
        setQueueStatus('Fetching seat map...')
      }
    }
  }

  // Convert SeatmapResult to SeatMapResponse format
  const convertResult = (
    wsResult: SeatmapResult,
    searchAirline: string,
    searchFlight: string,
    searchClass: string,
    searchDate: string,
    searchOrigin: string,
    searchDest: string
  ): SeatMapResponse => ({
    command: wsResult.command,
    raw_response: wsResult.raw_response,
    airline: wsResult.airline || searchAirline,
    flight_number: wsResult.flight_number || searchFlight,
    booking_class: searchClass,
    date: searchDate,
    origin: searchOrigin,
    destination: searchDest,
    aircraft: wsResult.aircraft || 'Unknown',
    cabin_sections: (wsResult.cabin_sections || []) as CabinSection[],
    legend: wsResult.legend || {},
  })

  // Reusable search function
  const performSearch = async (
    searchAirline: string,
    searchFlight: string,
    searchClass: string,
    searchDate: string,
    searchOrigin: string,
    searchDest: string
  ) => {
    // Check WebSocket connection
    if (wsStatus !== 'connected') {
      setError('WebSocket not connected. Please wait for reconnection.')
      return
    }

    setError(null)
    setResult(null)
    setIsLoading(true)
    setQueueStatus('Queuing...')

    try {
      const response = await sendSeatmapRequest(
        {
          airline: searchAirline,
          flight_number: searchFlight,
          booking_class: searchClass,
          date: searchDate,
          origin: searchOrigin,
          destination: searchDest,
        },
        handleProgress
      )

      // Convert to expected format
      setResult(convertResult(
        response,
        searchAirline,
        searchFlight,
        searchClass,
        searchDate,
        searchOrigin,
        searchDest
      ))
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unbekannter Fehler'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
      setQueueStatus(null)
    }
  }

  // Track if we've populated form from URL params (separate from hasAutoSearched)
  const hasPopulatedRef = useRef(false)

  // Effect 1: Populate form fields immediately from URL params (no WebSocket dependency)
  // This ensures the form shows the correct values even before WebSocket connects
  useEffect(() => {
    // Only populate once per mount
    if (hasPopulatedRef.current) return

    const {
      airline: pAirline,
      flight: pFlight,
      class: pClass,
      date: pDate,
      origin: pOrigin,
      destination: pDest
    } = searchParams

    // Populate any available params
    if (pAirline) setAirline(pAirline.toUpperCase())
    if (pFlight) setFlightNumber(pFlight)
    if (pClass) setBookingClass(pClass.toUpperCase())
    if (pDate) setDate(pDate.toUpperCase())
    if (pOrigin) setOrigin(pOrigin.toUpperCase())
    if (pDest) setDestination(pDest.toUpperCase())

    // Mark as populated if we had any params
    if (pAirline || pFlight || pClass || pDate || pOrigin || pDest) {
      hasPopulatedRef.current = true
    }
  }, [searchParams])

  // Effect 2: Auto-search when WebSocket is connected AND all params are present
  // This ensures search only happens after connection is established
  useEffect(() => {
    // Wait for WebSocket to be connected
    if (wsStatus !== 'connected') return
    // Don't search if we've already searched
    if (hasAutoSearched) return

    const {
      airline: pAirline,
      flight: pFlight,
      class: pClass,
      date: pDate,
      origin: pOrigin,
      destination: pDest
    } = searchParams

    // Only auto-search if ALL required params are present
    if (pAirline && pFlight && pClass && pDate && pOrigin && pDest) {
      // Mark as searched BEFORE triggering to prevent duplicate searches
      setHasAutoSearched(true)

      // Trigger search with WebSocket confirmed connected
      performSearch(
        pAirline.toUpperCase(),
        pFlight,
        pClass.toUpperCase(),
        pDate.toUpperCase(),
        pOrigin.toUpperCase(),
        pDest.toUpperCase()
      )
    }
  }, [wsStatus, searchParams, hasAutoSearched])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (!airline.trim() || airline.trim().length !== 2) {
      setError('Bitte geben Sie einen gültigen Airline-Code ein (2 Buchstaben)')
      return
    }
    if (!flightNumber.trim()) {
      setError('Bitte geben Sie eine Flugnummer ein')
      return
    }
    if (!origin.trim() || origin.trim().length !== 3) {
      setError('Bitte geben Sie einen gültigen Abflughafen-Code ein (3 Buchstaben)')
      return
    }
    if (!destination.trim() || destination.trim().length !== 3) {
      setError('Bitte geben Sie einen gültigen Zielflughafen-Code ein (3 Buchstaben)')
      return
    }
    if (!date.trim()) {
      setError('Bitte geben Sie ein Datum ein')
      return
    }

    await performSearch(
      airline.trim(),
      flightNumber.trim(),
      bookingClass,
      date.trim(),
      origin.trim(),
      destination.trim()
    )
  }

  // Quick class change - fetch same flight with different class
  const handleClassChange = async (newClass: string) => {
    if (!result) return

    setBookingClass(newClass)
    await performSearch(
      result.airline,
      result.flight_number,
      newClass,
      result.date,
      result.origin,
      result.destination
    )
  }

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Armchair className="h-8 w-8 text-action-500" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Seat Map</h1>
          <p className="text-slate-500">Sitzplatzkonfiguration und Verfügbarkeit</p>
        </div>
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
        <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4 items-end flex-wrap">
          <div className="w-24">
            <Label htmlFor="airline" className="text-sm font-medium text-slate-700 mb-1.5 block">
              Airline
            </Label>
            <Input
              id="airline"
              type="text"
              placeholder="LH"
              value={airline}
              onChange={(e) => setAirline(e.target.value.toUpperCase())}
              maxLength={2}
              className="uppercase font-mono"
            />
          </div>

          <div className="w-28">
            <Label htmlFor="flight" className="text-sm font-medium text-slate-700 mb-1.5 block">
              Flugnummer
            </Label>
            <Input
              id="flight"
              type="text"
              placeholder="401"
              value={flightNumber}
              onChange={(e) => setFlightNumber(e.target.value.replace(/\D/g, ''))}
              maxLength={4}
              className="font-mono"
            />
          </div>

          <div className="w-44">
            <Label htmlFor="class" className="text-sm font-medium text-slate-700 mb-1.5 block">
              Klasse
            </Label>
            <Select value={bookingClass} onValueChange={setBookingClass}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="F">First Class</SelectItem>
                <SelectItem value="C">Business</SelectItem>
                <SelectItem value="W">Premium Economy</SelectItem>
                <SelectItem value="M">Economy</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="w-28">
            <Label htmlFor="date" className="text-sm font-medium text-slate-700 mb-1.5 block">
              Datum
            </Label>
            <Input
              id="date"
              type="text"
              placeholder="20JAN"
              value={date}
              onChange={(e) => setDate(e.target.value.toUpperCase())}
              maxLength={5}
              className="uppercase font-mono"
            />
          </div>

          <div className="w-24">
            <Label htmlFor="origin" className="text-sm font-medium text-slate-700 mb-1.5 block">
              Von
            </Label>
            <Input
              id="origin"
              type="text"
              placeholder="JFK"
              value={origin}
              onChange={(e) => setOrigin(e.target.value.toUpperCase())}
              maxLength={3}
              className="uppercase font-mono"
            />
          </div>

          <div className="w-24">
            <Label htmlFor="destination" className="text-sm font-medium text-slate-700 mb-1.5 block">
              Nach
            </Label>
            <Input
              id="destination"
              type="text"
              placeholder="FRA"
              value={destination}
              onChange={(e) => setDestination(e.target.value.toUpperCase())}
              maxLength={3}
              className="uppercase font-mono"
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading || wsStatus !== 'connected'}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {queueStatus || 'Laden...'}
              </>
            ) : wsStatus !== 'connected' ? (
              <>
                <WifiOff className="h-4 w-4 mr-2" />
                Verbinden...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Suchen
              </>
            )}
          </Button>
        </form>

        <p className="text-xs text-slate-400 mt-3">
          Beispiel: LH 401 / C / 20JAN / JFK → FRA → Befehl: SMLH401/C/20JANJFKFRA/V
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Fehler</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="flex-1 overflow-auto">
          {/* Results Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Plane className="h-5 w-5 text-slate-400" />
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  {result.airline} {result.flight_number}
                </h2>
                <p className="text-sm text-slate-500">
                  {result.origin} → {result.destination} • {result.date} • Aircraft: {result.aircraft}
                </p>
              </div>
            </div>

            {/* Class selector for quick switching */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Kabine:</span>
              <div className="flex gap-1">
                {[
                  { code: 'F', label: 'First' },
                  { code: 'C', label: 'Business' },
                  { code: 'W', label: 'Prem Eco' },
                  { code: 'M', label: 'Economy' },
                ].map(({ code, label }) => (
                  <button
                    key={code}
                    onClick={() => handleClassChange(code)}
                    disabled={isLoading}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      result.booking_class === code
                        ? `${activeButtonClass} text-white shadow-sm`
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    title={label}
                  >
                    {code}
                  </button>
                ))}
              </div>
              <code className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500 font-mono ml-2">
                {result.command}
              </code>
            </div>
          </div>

          {/* Legend */}
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 mb-4">
            <SeatMapLegend />
          </div>

          {/* Cabin Sections */}
          {result.cabin_sections.length > 0 ? (
            result.cabin_sections.map((section, idx) => (
              <CabinSectionDisplay key={idx} section={section} />
            ))
          ) : (
            <div className="bg-slate-50 rounded-lg border border-slate-200 p-6 text-center">
              <Armchair className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Keine Sitzplatzdaten gefunden</p>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!result && !error && !isLoading && (
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-6 text-center">
          <Armchair className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">
            Geben Sie Flugdaten ein, um die Sitzplatzkonfiguration abzurufen
          </p>
        </div>
      )}
    </div>
  )
}

// Re-export the search params validator for use in routes
export function validateSeatMapSearch(search: Record<string, unknown>): SeatMapSearchParams {
  return {
    airline: typeof search.airline === 'string' ? search.airline : undefined,
    flight: typeof search.flight === 'string' ? search.flight : undefined,
    class: typeof search.class === 'string' ? search.class : undefined,
    date: typeof search.date === 'string' ? search.date : undefined,
    origin: typeof search.origin === 'string' ? search.origin : undefined,
    destination: typeof search.destination === 'string' ? search.destination : undefined,
  }
}
